import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, IconButton, Icon, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

import { useMaterialUIController } from "context";

import StudentSearch from "./StudentSearch";
import SelectedStudentHeader from "./SelectedStudentHeader";
import AntecedenteTab from "./AntecedenteTab";
import OtrosTab from "./OtrosTab";
import ExpedicionTab from "./ExpedicionTab";
import CarpetaSelect from "./CarpetaSelect";
import { useGetAntecedentesQuery } from "services/escuela/antecedentes_estudiantil/usecases/getAntecedentes";
import { useCreateTituloMutation } from "services/escuela/titulos/usecases/createTitulo";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1200,
  boxShadow: 24,
  borderRadius: "12px",
  px: 4,
  py: 2,
  outline: "none",
  maxHeight: "90vh",
  overflowY: "auto",
};

const validationSchema = Yup.object().shape({
  estudiante: Yup.string().required("El estudiante es requerido"),
  fundamento_legal: Yup.string().required("El fundamento legal es requerido"),
  motivo_cancelacion: Yup.string().required(
    "El motivo de cancelación es requerido",
  ),
  reconocimiento: Yup.string().required("El reconocimiento es requerido"),
  modalidad_de_titulacion: Yup.string().required(
    "La modalidad de titulación es requerida",
  ),
  expedition_date: Yup.date().required("La fecha de expedición es requerida"),
  fecha_examen_profesional: Yup.date().required(
    "La fecha de examen profesional es requerida",
  ),
  cumplio_servicio_social: Yup.boolean().required("Requerido"),
  firmante: Yup.string().required("El firmante es requerido"),
  tags: Yup.string(),
});

const initialValues = {
  estudiante: "",
  fundamento_legal: "",
  motivo_cancelacion: "",
  reconocimiento: "",
  modalidad_de_titulacion: "",
  expedition_date: "",
  fecha_examen_profesional: "",
  cumplio_servicio_social: false,
  firmante: "",
  tags: "",
};

const NewTitulo = ({ open, handleClose }) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();

  const [createTitulo] = useCreateTituloMutation();

  const { data: antecedentesData } = useGetAntecedentesQuery(
    {
      filter_params: {
        estudiante: selectedStudent?.id,
      },
      shape: {
        id: "id",
        institucion_procedencia: "institucion_procedencia",
        tipo_estudio_antecedente: "tipo_estudio_antecedente.descripcion",
        entidad_federativa_antecedente:
          "entidad_federativa_antecedente.entidad",
        fecha_inicio: "fecha_inicio",
        fecha_terminacion: "fecha_terminacion",
        no_cedula: "no_cedula",
      },
    },
    { skip: !selectedStudent?.id },
  );

  const antecedentesCount =
    antecedentesData?.results?.length ?? antecedentesData?.length ?? 0;

  const handleReset = () => {
    setSelectedStudent(null);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (antecedentesCount < 1) {
        enqueueSnackbar(
          "El estudiante debe tener al menos 1 antecedente para proceder",
          { variant: "error" },
        );
        setSubmitting(false);
        return;
      }

      // Limpiamos los campos vacíos antes de enviar a la API
      const payload = Object.fromEntries(
        Object.entries(values).filter(([_, v]) => v !== "" && v !== null),
      );

      if (payload.tags) {
        payload.tags = [payload.tags];
      } else {
        payload.tags = [];
      }

      await createTitulo(payload).unwrap();

      resetForm();
      handleReset();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Limpiar estado al cerrar el modal
  useEffect(() => {
    if (!open) {
      handleReset();
    }
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={modalStyle} bgColor={darkMode ? "black" : "white"}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <MDTypography
            variant="h5"
            fontWeight="medium"
            color={darkMode ? "white" : "dark"}
          >
            Crear Nuevo Título
          </MDTypography>
          <IconButton onClick={handleClose}>
            <Icon color={darkMode ? "white" : "dark"}>close</Icon>
          </IconButton>
        </MDBox>

        {!selectedStudent ? (
          <StudentSearch onSelectStudent={setSelectedStudent} />
        ) : (
          <MDBox>
            <SelectedStudentHeader
              student={selectedStudent}
              onReset={handleReset}
            />

            <Formik
              initialValues={{
                ...initialValues,
                estudiante: selectedStudent.url,
              }}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, handleSubmit }) => (
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  <MDBox mt={3}>
                    <CarpetaSelect />
                  </MDBox>

                  <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} md={4}>
                      <MDTypography
                        variant="h6"
                        color={darkMode ? "white" : "dark"}
                        mb={2}
                      >
                        Antecedente
                      </MDTypography>
                      <AntecedenteTab student={selectedStudent} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <MDTypography
                        variant="h6"
                        color={darkMode ? "white" : "dark"}
                        mb={2}
                      >
                        Otros Datos
                      </MDTypography>
                      <OtrosTab />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <MDTypography
                        variant="h6"
                        color={darkMode ? "white" : "dark"}
                        mb={2}
                      >
                        Expedición
                      </MDTypography>
                      <ExpedicionTab />
                    </Grid>
                  </Grid>

                  <MDBox display="flex" justifyContent="flex-end" mt={4}>
                    <MDButton
                      onClick={handleClose}
                      color="accent"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    >
                      Cancelar
                    </MDButton>
                    <MDButton
                      type="submit"
                      variant="contained"
                      color="accent"
                      disabled={isSubmitting}
                    >
                      Guardar Título
                    </MDButton>
                  </MDBox>
                </Form>
              )}
            </Formik>
          </MDBox>
        )}
      </MDBox>
    </Modal>
  );
};

NewTitulo.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default NewTitulo;
